import { atob } from 'js-base64'

export type ImageFormat =
  | 'apng'
  | 'avif'
  | 'gif'
  | 'jpeg'
  | 'png'
  | 'svg'
  | 'webp'

type ImageContentType = `image/${ImageFormat}`
type ResizerOutputType = 'blob' | 'base64' | 'file'

const changeDimensions = (
  height: number,
  maxHeight: number,
  width: number,
  maxWidth: number,
  minWidth: number,
  minHeight: number
) => {
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width)
    width = maxWidth
  }
  if (height > maxHeight) {
    width = Math.round((width * maxHeight) / height)
    height = maxHeight
  }
  if (minWidth && width < minWidth) {
    height = Math.round((height * minWidth) / width)
    width = minWidth
  }
  if (minHeight && height < minHeight) {
    width = Math.round((width * minHeight) / height)
    height = minHeight
  }
  return { height, width }
}

const resizeImageInCanvas = (
  image: HTMLImageElement,
  maxWidth: number,
  maxHeight: number,
  minWidth: number,
  minHeight: number,
  contentType: ImageContentType,
  quality = 100
) => {
  const qualityDecimal = quality / 100
  const canvas = document.createElement('canvas')

  let width = image.width
  let height = image.height

  const newHeightWidth = changeDimensions(
    height,
    maxHeight,
    width,
    maxWidth,
    minWidth,
    minHeight
  )
  canvas.width = newHeightWidth.width
  canvas.height = newHeightWidth.height
  width = newHeightWidth.width
  height = newHeightWidth.height

  const ctx = canvas.getContext('2d')
  if (contentType === 'image/jpeg') {
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, width, height)
  }
  if (ctx.imageSmoothingEnabled && ctx.imageSmoothingQuality) {
    ctx.imageSmoothingQuality = 'high'
  }
  ctx.drawImage(image, 0, 0, width, height)
  return canvas.toDataURL(contentType, qualityDecimal)
}

const b64toByteArrays = (b64Data: string) => {
  const sliceSize = 512

  // We don't want to use js-base64:decode here, because it will assume a utf-8 charset
  // but we have a binary file
  const byteCharacters = atob(
    b64Data.toString().replace(/^data:image\/(png|jpeg|jpg|webp);base64,/, '')
  )
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)

    byteArrays.push(byteArray)
  }
  return byteArrays
}

const b64toBlob = (b64Data: string, contentType: ImageContentType) => {
  const byteArrays = b64toByteArrays(b64Data)
  const blob = new Blob(byteArrays, {
    type: contentType,
  })
  return blob
}

const b64toFile = (
  b64Data: string,
  fileName: string,
  contentType: ImageContentType
) => {
  const byteArrays = b64toByteArrays(b64Data)
  const file = new File(byteArrays, fileName, {
    type: contentType,
  })
  return file
}

const imageResizeToFile = (
  file: Blob,
  fileExtension: ImageFormat,
  resizedDataUrl: string,
  contentType: ImageContentType,
  filename: string
) => {
  const fileNameWithoutExtension = filename
    .toString()
    .replace(/\.[0-9a-z]+$/i, '')
  const newFileName = fileNameWithoutExtension.concat(fileExtension)
  return b64toFile(resizedDataUrl, newFileName, contentType)
}

const imageFileResizer = (
  file: Blob,
  maxWidth: number,
  maxHeight: number,
  compressFormat: ImageFormat,
  quality: number,
  outputType: ResizerOutputType,
  filename?: string,
  minWidth = null,
  minHeight = null
): Promise<string | Blob | File> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    if (file) {
      if (file.type && !file.type.includes('image')) {
        throw Error('File Is NOT Image!')
      } else {
        reader.readAsDataURL(file)
        reader.onload = () => {
          const image = new Image()
          image.src = reader.result as string
          image.onload = function () {
            const contentType: ImageContentType = `image/${compressFormat}`
            const resizedDataUrl = resizeImageInCanvas(
              image,
              maxWidth,
              maxHeight,
              minWidth,
              minHeight,
              contentType,
              quality
            )
            switch (outputType) {
              case 'blob':
                resolve(b64toBlob(resizedDataUrl, contentType))
                break
              case 'base64':
                resolve(resizedDataUrl)
                break
              case 'file':
                resolve(
                  imageResizeToFile(
                    file,
                    compressFormat,
                    resizedDataUrl,
                    contentType,
                    filename || 'picture.jpeg'
                  )
                )
                break
              default:
                resolve(resizedDataUrl)
            }
          }
        }
        reader.onerror = () => {
          reject('Error reading image from canvas')
        }
      }
    } else {
      reject('File Not Found!')
    }
  })

const resize = (
  blob: Blob,
  width: number,
  height: number,
  filename?: string
): Promise<Blob> =>
  imageFileResizer(
    blob,
    width,
    height,
    'jpeg',
    60,
    'blob',
    filename
  ) as Promise<Blob>

const getImageSize = async (blob: Blob) => {
  const bmp = await createImageBitmap(blob)
  const { width, height } = bmp
  bmp.close()
  return { width, height }
}

export const resizeImage = async (
  blob: Blob,
  maxHeight: number,
  maxWidth: number,
  filename?: string
) => {
  const { width, height } = await getImageSize(blob)
  const imageRatio = width / height
  const maxImageRatio = maxWidth / maxHeight
  if (imageRatio >= maxImageRatio && height >= maxHeight) {
    return resize(blob, maxHeight * imageRatio, maxHeight, filename)
  } else if (imageRatio < maxImageRatio && width > maxWidth) {
    return resize(blob, maxWidth, maxWidth / imageRatio, filename)
  }
  return resize(blob, width, height, filename)
}
